import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { NavIcons } from "@/components/navIcons";
import AriaOneLogo from "@/components/AriaOneLogo";
import AriaOneEmblem from "@/components/AriaOneEmblem";
import {
  IconHome, IconTelemedicine, IconEncounterReview, IconDictation, IconLens,
  IconClinicDocuments, IconClaimsQueue, IconClaims, IconPaymentPosting, IconSwitch, IconSettings, IconSearch,
} from "@/components/icons/revqIcons";
import { IconUsers } from "@/lib/icons";
import { IconSidebarCollapse, IconSidebarExpand, IconCheck, IconPlus } from "@/components/icons/sidebarIcons";
import { useAuth } from "@/lib/auth";
import { useClient } from "@/lib/clientContext";
import { getNotesSummary } from "@/lib/noteClaim";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { TextSizeControl } from "./TextSizeControl";
import { LiveActivityPulse } from "@/components/LiveActivityPulse";
import { listSplitBatches } from "@/lib/patientDocs";

const CollapseCtx = React.createContext(false);

// Menu scope: 'api' shows only API-backed pages; 'full' shows everything.
// Persisted so the choice survives reloads. Defaults to 'api'.
const MENU_MODE_KEY = "revq.menu.mode";
function readMenuMode() {
  try { return localStorage.getItem(MENU_MODE_KEY) === "full" ? "full" : "api"; } catch { return "api"; }
}

// Menu count: "neutral" = waiting, "action" = need action. Used for tooltip text + collapsed dot.
const NAV_COUNT_ARIA = { neutral: "waiting", action: "need action", count: "need a patient" };

// ── Client picker (rich dropdown, shared by expanded + collapsed) ───────────
function ClientPickerBody({ clients, selectedId, connectionStateOf, onPick, onAdd }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const query = q.trim().toLowerCase();
  const match = query
    ? clients.filter((c) => String(c.client_id).includes(query) || (c.description || "").toLowerCase().includes(query))
    : null;
  const current = clients.find((c) => String(c.client_id) === String(selectedId));
  const rest = clients
    .filter((c) => String(c.client_id) !== String(selectedId))
    .sort((a, b) => String(a.client_id).localeCompare(String(b.client_id), undefined, { numeric: true }));
  const sections = match
    ? [{ label: "Clients", items: match }]
    : [...(current ? [{ label: "Current", items: [current] }] : []), { label: "All clients", items: rest }];
  const flat = sections.flatMap((s) => s.items);
  const selClamped = Math.max(0, Math.min(sel, flat.length - 1));

  const move = (d) => {
    const next = Math.max(0, Math.min(selClamped + d, flat.length - 1));
    setSel(next);
    requestAnimationFrame(() => listRef.current?.querySelector(`[data-idx="${next}"]`)?.scrollIntoView({ block: "nearest" }));
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[selClamped]) onPick(flat[selClamped]); }
  };

  let idx = 0;
  return (
    <div className="flex flex-col" data-testid="client-picker">
      <div className="flex items-center gap-[8px] h-[40px] px-[12px] text-[#9B9B97]" style={{ borderBottom: "1px solid #E9E9EC" }}>
        <IconSearch size={15} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setSel(0); }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls="client-picker-list"
          autoComplete="off"
          placeholder="Search by client ID or name"
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[13px] text-[#141412] placeholder:text-[#9B9B97]"
          data-testid="client-picker-search"
        />
      </div>
      <div ref={listRef} id="client-picker-list" role="listbox" aria-label="Clients" className="max-h-[320px] overflow-y-auto p-[4px]" data-testid="client-picker-list">
        {flat.length === 0 ? (
          <div className="px-[8px] py-[14px] text-[12.5px] text-[#6C6C69]" data-testid="client-picker-empty">No client matches “{q}”.</div>
        ) : (
          sections.map((g) => (
            <div key={g.label}>
              <div className="px-[8px] pt-[8px] pb-[4px] text-[11px] font-semibold text-[#6E6E6B]">{g.label}</div>
              {g.items.map((c) => {
                const k = idx++;
                const isCurrent = String(c.client_id) === String(selectedId);
                const connected = connectionStateOf(c.client_id) === "connected";
                const n = c.linked_npi_count || 0;
                return (
                  <button
                    key={c.client_id}
                    type="button"
                    role="option"
                    aria-selected={k === selClamped}
                    aria-current={isCurrent ? "true" : undefined}
                    data-idx={k}
                    data-testid={`client-picker-option-${c.client_id}`}
                    onMouseEnter={() => setSel(k)}
                    onClick={() => onPick(c)}
                    className={`w-full flex items-center gap-[10px] px-[8px] py-[7px] rounded-[8px] text-left ${k === selClamped ? "bg-[#F4F4F9]" : ""}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold leading-[1.25] text-[#141412] truncate tabular-nums">Client {c.client_id}</span>
                      <span className="block mt-px text-[11.5px] leading-[1.25] text-[#6C6C69] truncate">{c.description || "No description"} · {n} provider{n === 1 ? "" : "s"}</span>
                    </span>
                    {connected ? (
                      <img src="/enable_healthcare_emblem.png" alt="" title="Connected with Enable Healthcare" aria-label="Connected with Enable Healthcare" className="w-[14px] h-[14px] rounded-full flex-none" data-testid={`client-picker-enable-${c.client_id}`} />
                    ) : null}
                    <span className="w-[16px] flex-none grid place-items-center text-[#141412]">{isCurrent ? <IconCheck size={16} /> : null}</span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
      <div className="p-[4px]" style={{ borderTop: "1px solid #E9E9EC" }}>
        <button type="button" onClick={onAdd} data-testid="client-picker-add"
          className="w-full flex items-center gap-[10px] h-[34px] px-[8px] rounded-[8px] text-[13px] text-[#141412] hover:bg-[#F4F4F9]">
          <span className="text-[#6C6C69] flex-none"><IconPlus size={16} /></span>Add client
        </button>
      </div>
      <div className="flex gap-[10px] px-[12px] pt-[6px] pb-[8px] text-[11px] text-[#9B9B97]" style={{ borderTop: "1px solid #E9E9EC" }}>
        <span>↑ ↓ to move</span><span>Enter to switch</span><span>Esc to close</span>
      </div>
    </div>
  );
}

// Unified client + connection block. Opens the rich picker below the box (expanded)
// or beside the 40×40 button (collapsed). Switching keeps the confirmation dialog.
function SidebarClient({ tenant, collapsed }) {
  const { clients, selectedClientId, setSelectedClientId, connectionState, connectionStateOf } = useClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [flash, setFlash] = useState(false);
  const selected = useMemo(() => clients.find((c) => String(c.client_id) === String(selectedClientId)), [clients, selectedClientId]);
  const isConnected = connectionState === "connected";
  const multi = clients.length > 1;

  const pick = (c) => {
    setOpen(false);
    if (String(c.client_id) !== String(selectedClientId)) setPending(c);
  };
  const confirmSwitch = () => {
    const c = pending;
    setPending(null);
    setSelectedClientId(c.client_id);
    setFlash(true);
    setTimeout(() => setFlash(false), 1600);
    toast.success(`Now working in Client ${c.client_id}`, { description: c.description || "All pages are now scoped to this client." });
  };
  const addClient = () => { setOpen(false); navigate("/settings?step=clients"); };

  const picker = (
    <ClientPickerBody
      clients={clients}
      selectedId={selectedClientId}
      connectionStateOf={connectionStateOf}
      onPick={pick}
      onAdd={addClient}
    />
  );
  const contentProps = collapsed
    ? { side: "right", align: "start", sideOffset: 10 }
    : { side: "bottom", align: "start", sideOffset: 6 };

  return (
    <>
      <style>{`@keyframes scFlash{0%{box-shadow:0 0 0 0 rgba(20,20,18,.45)}100%{box-shadow:0 0 0 10px rgba(20,20,18,0)}}.sc-flash{animation:scFlash .8s ease-out 2}`}</style>
      <Popover open={open} onOpenChange={setOpen}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="sidebar-client-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={open}
                  aria-label={`Switch client, current Client ${selected?.client_id || ""}`}
                  className={`relative flex-none mx-auto w-[40px] h-[40px] mb-[10px] grid place-items-center rounded-[10px] text-[#9B9B97] hover:bg-[#F4F4F9] hover:text-[#141412] data-[state=open]:bg-[#F4F4F9] data-[state=open]:text-[#141412] ${flash ? "sc-flash" : ""}`}
                  style={{ border: "1px solid #E9E9EC" }}
                >
                  <IconSwitch size={16} />
                  {isConnected ? (
                    <img src="/enable_healthcare_emblem.png" alt="" className="absolute w-[14px] h-[14px] rounded-full" style={{ right: -4, bottom: -4, boxShadow: "0 0 0 2px #fff" }} data-testid="sidebar-client-enable-badge" />
                  ) : null}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10} className="rounded-[6px] px-2 py-1 text-[12px] font-medium" style={{ background: "#141412", color: "#fff" }}>
              Client {selected?.client_id}{selected?.description ? ` · ${selected.description}` : ""}
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className={`flex-none mb-[10px] rounded-[10px] overflow-hidden ${flash ? "sc-flash" : ""}`} style={{ border: "1px solid #E9E9EC", background: "#fff" }} data-testid="sidebar-connection-block">
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="sidebar-client-trigger"
                title={multi ? `${clients.length} clients on this account` : undefined}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="w-full flex items-center gap-[8px] text-left px-[10px] py-[7px] hover:bg-[#F4F4F9] data-[state=open]:bg-[#F4F4F9] transition-colors"
              >
                <span className="min-w-0 flex-1">
                  {tenant ? <span data-testid="sidebar-client-tenant" title={tenant} className="block text-[11px] text-[#6E6E6B] truncate">{tenant}</span> : null}
                  {selected ? (
                    <>
                      <span data-testid="sidebar-client-current" title={`Client ${selected.client_id}`} className="block mt-px text-[13px] font-bold text-[#141412] tabular-nums truncate leading-[1.25]">Client {selected.client_id}</span>
                      {selected.description ? <span data-testid="sidebar-client-desc" title={selected.description} className="block mt-px text-[11px] text-[#6C6C69] truncate leading-[1.25]">{selected.description}</span> : null}
                    </>
                  ) : <span className="block mt-px text-[13px] text-[#9B9B97] truncate leading-[1.25]">Select client</span>}
                </span>
                <span className="flex-none text-[#9B9B97]" data-testid="sidebar-client-switch-icon"><IconSwitch size={16} /></span>
              </button>
            </PopoverTrigger>
            {isConnected ? (
              <div
                data-testid="sidebar-connection-row"
                className="flex items-center gap-1.5 text-[10.5px] text-[#6E6E6B] px-[10px] py-[7px] whitespace-nowrap overflow-hidden"
                style={{ background: "#FBFBFD", borderTop: "1px solid #F1F1F4" }}
              >
                <img src="/enable_healthcare_emblem.png" alt="" className="w-[14px] h-[14px] rounded-full flex-shrink-0" />
                Connected with Enable Healthcare
              </div>
            ) : null}
          </div>
        )}
        <PopoverContent {...contentProps} className="w-[288px] p-0 rounded-[10px] overflow-hidden" style={{ border: "1px solid #E9E9EC", boxShadow: "0 10px 28px rgba(20,20,18,.14)" }} data-testid="sidebar-client-menu">
          {picker}
        </PopoverContent>
      </Popover>
      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent data-testid="client-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="client-switch-title">Switch to Client {pending?.client_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              Claims, patients, documents and payments will re-scope to Client {pending?.client_id}{pending?.description ? ` (${pending.description})` : ""}. You're currently in Client {selected?.client_id}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="client-switch-cancel">Stay in Client {selected?.client_id}</AlertDialogCancel>
            <AlertDialogAction data-testid="client-switch-confirm" onClick={confirmSwitch}>Switch to Client {pending?.client_id}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function NavRow({ to, icon: Icon, children, badge, hot, badgeTone, badgeTitle, testid, forceActive, dev, splitting, iconSize = 18, iconStroke = 1.75 }) {
  const collapsed = useContext(CollapseCtx);
  if (dev) {
    if (collapsed) {
      return (
        <div data-testid={testid} aria-disabled="true" title={`${children} — under development`}
          className="flex-none mx-auto w-[40px] h-[34px] grid place-items-center mb-px rounded-[8px] text-[#a3a3a1] opacity-60 cursor-not-allowed select-none">
          <Icon size={iconSize} stroke={iconStroke} />
        </div>
      );
    }
    return (
      <div data-testid={testid} aria-disabled="true" title={`${children} — under development`}
        className="flex-none flex items-center h-[34px] gap-[10px] px-[10px] mb-px rounded-[8px] text-[13.5px] text-[#a3a3a1] opacity-60 cursor-not-allowed select-none">
        <Icon size={iconSize} stroke={iconStroke} />
        <span className="flex-1 truncate">{children}</span>
        <span data-testid={testid ? `${testid}-dev-flag` : undefined} className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-px rounded-[9px] border border-dashed border-black/20 text-[#8a8a88] flex-shrink-0 ml-3 whitespace-nowrap">Under dev</span>
      </div>
    );
  }
  const showDot = collapsed && badgeTone === "action" && badge > 0;
  const tipText = `${children}${badge > 0 && badgeTone ? ` · ${badge} ${NAV_COUNT_ARIA[badgeTone]}` : ""}`;
  const cls = ({ isActive }) => {
    const active = isActive || forceActive;
    return collapsed
      ? `relative flex-none mx-auto w-[40px] h-[34px] flex items-center justify-center mb-px rounded-[8px] transition-colors ${active ? "bg-[#141412]" : "hover:bg-[#F4F4F9]"}`
      : `flex-none flex items-center h-[34px] gap-[10px] px-[10px] mb-px rounded-[8px] text-[13.5px] transition-colors ${active ? "bg-[#141412] text-white font-semibold" : "text-[#141412] hover:bg-[#F4F4F9]"}`;
  };
  const link = (
    <NavLink to={to} className={cls} data-testid={testid} aria-label={collapsed ? tipText : undefined} end onClick={() => window.dispatchEvent(new CustomEvent("revq:nav-page"))}>
      {({ isActive }) => {
        const active = isActive || forceActive;
        return (
          <>
            <span className="flex-none" style={{ color: active ? "#FFFFFF" : "#6C6C69" }}><Icon size={iconSize} stroke={iconStroke} /></span>
            {showDot ? <span aria-hidden className="absolute top-[6px] right-[9px] w-[8px] h-[8px] rounded-full" style={{ background: "#E5484D", boxShadow: `0 0 0 2px ${active ? "#141412" : "#fff"}` }} data-testid={testid ? `${testid}-dot` : undefined} /> : null}
            {!collapsed && <span className="flex-1 truncate">{children}</span>}
            {!collapsed && badgeTone ? <NavCount n={badge} tone={badgeTone} active={active} title={badgeTitle} testid={testid ? `${testid}-badge` : undefined} /> : null}
            {!collapsed && badge != null && !badgeTone ? (
              <span
                data-testid={testid ? `${testid}-badge` : undefined}
                title={splitting ? `${badge} combined scan${badge === 1 ? "" : "s"} still splitting into documents` : undefined}
                className={`${splitting ? "inline-flex items-center gap-1 " : ""}text-[9px] font-bold px-1.5 py-px rounded-[9px] border min-w-[18px] text-center flex-shrink-0 ml-3 ${
                  hot
                    ? active ? "bg-white text-black border-transparent" : "bg-[#111110] text-white border-transparent"
                    : active ? "bg-white/[.24] text-white border-transparent" : "bg-black/[.07] text-[#48484a] border-black/[.08]"
                }`}
              >
                {splitting ? <span className="w-[5px] h-[5px] rounded-full bg-[#4A4AC2] animate-pulse" aria-hidden="true" /> : null}
                {badge}
              </span>
            ) : null}
          </>
        );
      }}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10} className="rounded-[6px] px-2 py-1 text-[12px] font-medium" style={{ background: "#141412", color: "#fff" }}>{tipText}</TooltipContent>
    </Tooltip>
  );
}

// Menu count: "neutral" = plain grey number, "action" = red pill. 0 renders nothing.
function NavCount({ n, tone, active, title, testid }) {
  if (!n) return null;
  const aria = `${n} ${NAV_COUNT_ARIA[tone]}`;
  if (tone === "action") {
    return <span data-testid={testid} aria-label={aria} className="ml-auto flex-none self-center inline-flex items-center justify-center h-[20px] min-w-[20px] px-[6px] rounded-[999px] text-[12px] font-semibold tabular-nums" style={active ? { background: "#FFFFFF", color: "#A3282C" } : { background: "#E5484D", color: "#FFFFFF" }}>{n}</span>;
  }
  if (tone === "count") {
    return <span data-testid={testid} aria-label={aria} title={title} className="ml-auto flex-none self-center text-[12px] font-semibold tabular-nums" style={{ color: active ? "rgba(255,255,255,.7)" : "#6E6E6B" }}>{n}</span>;
  }
  return <span data-testid={testid} aria-label={aria} className="ml-auto flex-none self-center text-[13px] font-normal tabular-nums" style={{ color: active ? "rgba(255,255,255,.7)" : "#6C6C69" }}>{n}</span>;
}

function NavSection({ title, product, children }) {
  const collapsed = useContext(CollapseCtx);
  return (
    <div className={`flex-none flex flex-col ${collapsed ? "items-center" : ""}`}>
      {/* collapsed: a 40×17 block with a centred 16×1 hairline as the group divider */}
      {collapsed && title ? <div aria-hidden className="flex-none w-[40px] h-[17px] grid place-items-center"><span className="w-[16px] h-px bg-[#E9E9EC]" /></div> : null}
      {!collapsed && title ? (
        <div className="flex-none pt-[14px] px-[10px] pb-[5px] text-[11px] font-semibold text-[#6E6E6B] truncate">
          {title}{product ? <span className="ml-[5px] font-normal text-[#9B9B97]">· {product}</span> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export default function Sidebar({ activeChart, collapsed = false, onToggle } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "Account";

  const [menuMode, setMenuMode] = useState(readMenuMode);
  const showFull = menuMode === "full";
  const setMode = (m) => { setMenuMode(m); try { localStorage.setItem(MENU_MODE_KEY, m); } catch {} };

  // ⌘\ / Ctrl+\ toggles the menu.
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "\\") { e.preventDefault(); onToggle?.(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onToggle]);

  // §3.5 — "codes out of date" count for the Claims Queue nav badge (polled).
  const { selectedClientId } = useClient();
  const [staleCount, setStaleCount] = useState(0);
  const [unsignedCount, setUnsignedCount] = useState(0);
  // Nav badges — one lightweight /notes/summary call (no row bodies, ETag-cached) covers both
  // the codes-stale and unsigned counts; replaces two separate "fetch a page to read a count" polls.
  useEffect(() => {
    let alive = true;
    if (!selectedClientId) { setStaleCount(0); setUnsignedCount(0); return undefined; }
    const tick = () => getNotesSummary(selectedClientId).then((s) => {
      if (!alive) return;
      setStaleCount(s.codes_stale || 0);
      setUnsignedCount(s.unsigned || 0);
    }).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [selectedClientId]);

  // Combined scans still splitting → badge on Chart / Clinic documents (8 s while busy, else 60 s).
  const [splitting, setSplitting] = useState(0);
  useEffect(() => {
    let alive = true, t = null;
    if (!selectedClientId) { setSplitting(0); return undefined; }
    const tick = async () => {
      clearTimeout(t);
      let n = 0;
      try { const bs = await listSplitBatches({ client_id: selectedClientId }); n = bs.filter((b) => b.status === "processing" || (b.segments || []).some((s) => s.status === "queued" || s.status === "processing")).length; } catch { /* keep last */ n = null; }
      if (!alive) return;
      if (n != null) setSplitting(n);
      t = setTimeout(tick, n ? 8000 : 60000);
    };
    tick();
    const kick = () => setTimeout(tick, 1500);
    window.addEventListener("revq:split-started", kick); window.addEventListener("focus", tick);
    return () => { alive = false; clearTimeout(t); window.removeEventListener("revq:split-started", kick); window.removeEventListener("focus", tick); };
  }, [selectedClientId]);
  // §3 — "needs a patient" count for the Chart Documents nav badge. Published by the
  // documents page from the rows it already loads (no extra API call); reset on client switch.
  const [needsPatient, setNeedsPatient] = useState(0);
  useEffect(() => {
    const cached = typeof window !== "undefined" ? window.__revqDocsNeeds : null;
    setNeedsPatient(cached && cached.clientId === selectedClientId ? (cached.needs || 0) : 0);
  }, [selectedClientId]);
  useEffect(() => {
    const onNeeds = (e) => { const d = e.detail || {}; if (d.clientId === selectedClientId) setNeedsPatient(d.needs || 0); };
    window.addEventListener("revq:docs-needs", onNeeds);
    return () => window.removeEventListener("revq:docs-needs", onNeeds);
  }, [selectedClientId]);
  const needsBadge = needsPatient || null;
  const needsTitle = needsBadge ? `${needsBadge} document${needsBadge === 1 ? " needs" : "s need"} a patient` : undefined;

  return (
    <CollapseCtx.Provider value={collapsed}>
      <TooltipProvider delayDuration={250}>
        <nav
          className={`${collapsed ? "w-[64px] min-w-[64px] pt-[18px] pb-[12px]" : "w-[264px] min-w-[264px] pt-[18px] px-[12px] pb-[12px]"} rq-sidenav flex flex-col overflow-hidden bg-white`}
          style={{ borderRight: "1px solid #E9E9EC", borderRadius: 0, background: "#FFFFFF" }}
          data-testid="app-sidebar"
          data-collapsed={collapsed ? "1" : "0"}
        >
          {/* Brand / header */}
          {collapsed ? (
            <div className="flex-none flex flex-col items-center">
              <div className="flex items-center justify-center" style={{ height: 26, margin: "2px 0 14px" }}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onToggle?.()}
                      aria-label="Expand menu"
                      data-testid="sidebar-expand-toggle"
                      className="group relative w-[40px] h-[26px] rounded-[8px] grid place-items-center hover:bg-[#F4F4F9]"
                    >
                      <AriaOneEmblem height={18} className="transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0" style={{ transitionDuration: "120ms" }} />
                      <span className="absolute inset-0 grid place-items-center text-[#141412] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" style={{ transitionDuration: "120ms" }}><IconSidebarExpand size={18} /></span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={10} className="rounded-[6px] px-2 py-1 text-[12px] font-medium" style={{ background: "#141412", color: "#fff" }}>
                    Expand menu<span className="ml-[6px] text-white/60">⌘\</span>
                  </TooltipContent>
                </Tooltip>
              </div>
              <SidebarClient collapsed tenant={fullName} />
            </div>
          ) : (
            <div className="flex-none flex flex-col gap-4" data-testid={TID.sidebarBrand}>
              <div data-testid="sidebar-logo" style={{ padding: "2px 10px 14px" }}>
                <div className="flex items-center">
                  <button type="button" onClick={() => navigate("/home")} className="flex items-center" aria-label="Home"><AriaOneLogo height={26} /></button>
                  {onToggle ? <button type="button" onClick={onToggle} className="rq-navtgl ml-auto w-7 h-7 rounded-[8px] grid place-items-center text-[#9B9B97] hover:bg-[#F4F4F9] hover:text-[#141412]" aria-label="Collapse menu" title="Collapse menu  ⌘\" data-testid="sidebar-collapse-toggle"><IconSidebarCollapse size={18} /></button> : null}
                </div>
                {user?.tenant_code ? <div data-testid="sidebar-cluster-id" title={`Aria Cluster ID: ${user.tenant_code}`} className="mt-1.5 text-[11px] font-semibold text-[#6E6E6B] truncate">Aria Cluster ID: {user.tenant_code}</div> : null}
              </div>
              <div className="min-w-0">
                <SidebarClient tenant={fullName} />
              </div>
            </div>
          )}

          {/* Scrollable middle — only this area scrolls, brand + footer stay pinned */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col" style={{ scrollbarWidth: "none" }} data-testid="sidebar-nav-list">
            {/* API-supported menu — §1.1 order */}
            {!showFull && (
              <>
                <NavSection>
                  <NavRow to="/home" icon={IconHome} testid={TID.navDashboard}>Home</NavRow>
                  <NavRow to="/patients" icon={IconUsers} testid="nav-patients">Patients</NavRow>
                </NavSection>
                <NavSection title="Scheduling" product="Echo">
                  <NavRow to="/telehealth-v2" icon={IconTelemedicine} testid="nav-telehealth-v2">Telehealth</NavRow>
                </NavSection>
                <NavSection title="Clinical" product="Lumina">
                  <NavRow to="/encounter-review" icon={IconEncounterReview} testid="nav-encounter-review" badge={unsignedCount} badgeTone="neutral">Encounter review</NavRow>
                  <NavRow to="/dictation" icon={IconDictation} testid="nav-dictation">Dictation</NavRow>
                  <NavRow to="/patient-documents" icon={IconLens} testid="nav-patient-documents" badge={needsBadge} badgeTone="count" badgeTitle={needsTitle}>Chart documents</NavRow>
                  <NavRow to="/clinic-documents" icon={IconClinicDocuments} testid="nav-clinic-documents">Clinic documents</NavRow>
                </NavSection>
                <NavSection title="Billing" product="revQ">
                  <NavRow to="/create-claims" icon={IconClaimsQueue} testid="nav-create-claims" badge={staleCount} badgeTone="action">Claims queue</NavRow>
                  <NavRow icon={IconClaims} dev testid="nav-manage-claims">Claims</NavRow>
                  <NavRow to="/payments-v2" icon={IconPaymentPosting} testid="nav-payments-v2">Payment posting</NavRow>
                </NavSection>
              </>
            )}

            {/* Full menu — same §1.1 groups + the extra pages */}
            {showFull && (
              <>
                <NavSection>
                  <NavRow to="/home" icon={IconHome} testid={TID.navDashboard}>Home</NavRow>
                  <NavRow to="/patients" icon={IconUsers} testid="nav-patients">Patients</NavRow>
                </NavSection>
                <NavSection title="Scheduling" product="Echo">
                  <NavRow to="/telehealth-v2" icon={IconTelemedicine} testid="nav-telehealth-v2">Telehealth</NavRow>
                </NavSection>
                <NavSection title="Clinical" product="Lumina">
                  <NavRow to="/encounter-review" icon={IconEncounterReview} testid="nav-encounter-review" badge={unsignedCount} badgeTone="neutral">Encounter review</NavRow>
                  <NavRow to="/dictation" icon={IconDictation} testid="nav-dictation">Dictation</NavRow>
                  <NavRow to="/patient-documents" icon={IconLens} testid="nav-patient-documents" badge={needsBadge} badgeTone="count" badgeTitle={needsTitle}>Chart documents</NavRow>
                  <NavRow to="/clinic-documents" icon={IconClinicDocuments} testid="nav-clinic-documents">Clinic documents</NavRow>
                  <NavRow icon={NavIcons.attachments} to="/lumina-documents" testid="nav-lumina-documents">Lumina Documents</NavRow>
                </NavSection>
                <NavSection title="Billing" product="revQ">
                  <NavRow to="/create-claims" icon={IconClaimsQueue} testid="nav-create-claims" badge={staleCount} badgeTone="action">Claims queue</NavRow>
                  <NavRow icon={IconClaims} dev testid="nav-manage-claims">Claims</NavRow>
                  <NavRow to="/payments-v2" icon={IconPaymentPosting} testid="nav-payments-v2">Payment posting</NavRow>
                  <NavRow to="/eligibility" icon={NavIcons.eligibility} testid="nav-eligibility">Eligibility</NavRow>
                  <NavRow to="/authorizations" icon={NavIcons.authorizations} dev testid="nav-authorizations">Authorizations</NavRow>
                  <NavRow to="/denials" icon={NavIcons.denials} dev testid="nav-denials">Denials &amp; Recovery</NavRow>
                  <NavRow to="/patient-billing" icon={NavIcons.patientBilling} dev testid="nav-patient-billing">Patient Billing</NavRow>
                </NavSection>
                <NavSection title="Records">
                  <NavRow to="/records/claims" icon={NavIcons.claims} testid="nav-claims">Claims</NavRow>
                  <NavRow to="/records/encounters" icon={NavIcons.encounters} testid="nav-encounters">Encounters</NavRow>
                  <NavRow to="/records/superbills" icon={NavIcons.superbills} testid="nav-superbills">Superbills</NavRow>
                </NavSection>
                <NavSection title="Insights">
                  <NavRow to="/recommendations" icon={NavIcons.recommendations} badge={6} hot testid="nav-recommendations">Recommendations</NavRow>
                  <NavRow to="/analytics" icon={NavIcons.analytics} testid="nav-analytics">Analytics</NavRow>
                  <NavRow to="/rules" icon={NavIcons.rules} badge={5} testid="nav-rules">Rules</NavRow>
                </NavSection>
              </>
            )}
          </div>

          {/* Footer — agent status, (expanded: menu scope + alerts,) text size, settings */}
          <div className={`flex-none flex flex-col gap-[2px] mt-[8px] pt-[10px] ${collapsed ? "items-center" : ""}`} style={{ borderTop: "1px solid #E9E9EC" }} data-testid="sidebar-footer">
            <LiveActivityPulse collapsed={collapsed} docsBusy={splitting} />
            {!collapsed && (
              <div data-testid="sidebar-menu-mode" className="mb-2.5">
                <div className="text-[9px] font-semibold tracking-[.11em] text-[#aeaeb2] uppercase px-[7px] mb-[5px]">Menu</div>
                <div className="flex gap-[3px] p-[3px] rounded-[10px] bg-black/[.045]">
                  <button
                    data-testid="menu-mode-api"
                    onClick={() => setMode("api")}
                    aria-pressed={!showFull}
                    className={`flex-1 py-[5px] rounded-[7px] text-[10.5px] font-semibold transition-all ${!showFull ? "bg-white text-[#111110] shadow-[0_1px_4px_rgba(0,0,0,.14)]" : "text-[#6E6E6B] hover:text-[#111110]"}`}
                  >
                    API supported
                  </button>
                  <button
                    data-testid="menu-mode-full"
                    onClick={() => setMode("full")}
                    aria-pressed={showFull}
                    className={`flex-1 py-[5px] rounded-[7px] text-[10.5px] font-semibold transition-all ${showFull ? "bg-white text-[#111110] shadow-[0_1px_4px_rgba(0,0,0,.14)]" : "text-[#6E6E6B] hover:text-[#111110]"}`}
                  >
                    Full
                  </button>
                </div>
              </div>
            )}
            {!collapsed && showFull && (
              <button
                data-testid="sidebar-alerts"
                onClick={() => navigate("/denials")}
                className="w-full flex items-center gap-2 mb-1.5 px-2.5 py-2 rounded-[9px] bg-black/[.035] hover:bg-black/[.06] transition text-left"
              >
                <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background: "#FF9500" }} />
                <span className="flex-1 min-w-0 text-[11.5px] text-[#48484a] truncate">revQ alerts</span>
                <span className="text-[11px] font-semibold text-[#5856D6] flex-shrink-0">Review all →</span>
              </button>
            )}
            <TextSizeControl collapsed={collapsed} />
            <NavRow to="/settings" icon={IconSettings} testid="nav-system">Settings</NavRow>
          </div>
        </nav>
      </TooltipProvider>
    </CollapseCtx.Provider>
  );
}
